import { Callout } from "@/shared/foundation/primitives";

import styles from "./DeviceFailureAlert.module.css";

interface DeviceFailureAlertProps {
  /** How many devices the last attempt acted on. */
  attempted: number;
  /** The last attempt's failures: each failed device's ID and the reason. */
  messages: ReadonlyMap<string, string>;
  /**
   * Whether the dialog opened on several devices. Stays true when a retry
   * sends only one, so the alert keeps naming the device that failed.
   */
  isBulk: boolean;
  /** Ends the bulk summary: "N of M devices could not be {outcome}." */
  outcome: "deleted" | "changed";
}

/**
 * Shows the failures of a device dialog's last attempt: for a single device,
 * the failure's message alone; for a bulk dialog, a count and a list naming
 * each failed device with its reason.
 */
export function DeviceFailureAlert({
  attempted,
  messages,
  isBulk,
  outcome,
}: DeviceFailureAlertProps) {
  if (messages.size === 0) {
    return null;
  }
  if (!isBulk) {
    return <Callout variant="error">{[...messages.values()][0]}</Callout>;
  }
  return (
    <Callout variant="error">
      {messages.size} of {attempted} {attempted === 1 ? "device" : "devices"}{" "}
      could not be {outcome}.
      <ul className={styles.failureList}>
        {[...messages].map(([deviceId, message]) => (
          <li key={deviceId}>
            <code>{deviceId}</code>: {message}
          </li>
        ))}
      </ul>
    </Callout>
  );
}
